import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { resolveSiteUrl } from '@/lib/site-url'

import { siteConfig } from '@/constants/site'

// JSON-LD is embedded inside a script tag, so "<" is escaped to keep stored text from closing it.
const serializeJsonLd = (data: Record<string, unknown>): string => JSON.stringify(data).replace(/</g, '\\u003c')

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    path = '/',
    siteUrl,
    noindex = false,
    jsonLd = []
}): ReactNode => {
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const base = resolveSiteUrl(siteUrl)
    const canonical = path === '/' ? `${base}/` : `${base}${path}`
    const image = ogImage || `${base}/api/og`

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <link rel='canonical' href={canonical} />
            {noindex && <meta name='robots' content='noindex, follow' />}

            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content='website' />
            <meta property='og:url' content={canonical} />
            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:locale' content={siteConfig.locale} />
            <meta property='og:image' content={image} />
            {!ogImage && <meta property='og:image:width' content='1200' />}
            {!ogImage && <meta property='og:image:height' content='630' />}

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />
            <meta name='twitter:image' content={image} />

            {jsonLd.map((item, index) => (
                <script
                    key={`json-ld-${index}`}
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: serializeJsonLd(item) }}
                />
            ))}
        </Head>
    )
}
